import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ion } from "@/components/Ion";
import { api } from "@/lib/api";
import { inputCls } from "@/components/quote/QuoteParts";

const Chip = ({ onClick, children, testId, on }) => (
  <button onClick={onClick} data-testid={testId} className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${on ? "border-[#4CCC06] bg-[#4CCC06] text-black" : "border-[#444] text-white hover:border-[#4CCC06]"}`}>{children}</button>
);

// Search Invoicing customers (name, company, job # or quote #) and attach this builder quote to a customer and job.
export const LinkBar = ({ link, setLink }) => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [editing, setEditing] = useState(!link);
  useEffect(() => { if (link) setEditing(false); }, [link]);
  useEffect(() => {
    if (!q.trim()) return setRes([]);
    const t = setTimeout(() => api(`/quote-builder/link-search?q=${encodeURIComponent(q.trim())}`).then(setRes).catch((e) => toast.error(e.message)), 300);
    return () => clearTimeout(t);
  }, [q]);
  const pick = (c, j) => { setLink({ customer_id: c.id, customer_name: c.company ? `${c.company} · ${c.name}` : c.name, job_id: j?.id || null, job_number: j?.number || null, job_title: j?.title || "" }); setEditing(false); setQ(""); };

  if (link && !editing) return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[12px] border border-[#4CCC06] bg-[#4CCC060D] px-3 py-2.5" data-testid="link-bar">
      <Ion name="link-outline" size={16} color="#4CCC06" />
      <p className="min-w-0 flex-1 text-[13px]" data-testid="link-summary"><b>{link.customer_name}</b>{link.job_id ? <span className="text-[#A0A0A0]"> · Job {link.job_number ? `#${link.job_number}` : ""} {link.job_title || "attached"}</span> : <span className="text-[#A0A0A0]"> · no job</span>}</p>
      <button onClick={() => nav(link.job_id ? `/billing/job/${link.job_id}` : `/billing?tab=customers&customer=${link.customer_id}`)} data-testid="link-open-invoicing" className="text-[11px] font-extrabold text-[#4CCC06] hover:underline">Open in Invoicing →</button>
      <button onClick={() => setEditing(true)} data-testid="link-change" className="text-[11px] font-bold text-[#A0A0A0] hover:text-white">Change</button>
      <button onClick={() => { setLink(null); setEditing(true); }} data-testid="link-remove" className="text-[11px] font-bold text-[#A0A0A0] hover:text-[#EF4444]">Detach</button>
    </div>
  );
  return (
    <div className="mb-3 rounded-[12px] border border-[#333] bg-[#1A1A1A] p-3" data-testid="link-bar">
      <p className="mb-1.5 flex items-center gap-2 text-[13px] font-extrabold"><Ion name="link-outline" size={16} color="#4CCC06" />Attach to a customer &amp; job</p>
      <input className={inputCls} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer name, company, job # or quote #" data-testid="link-search" />
      {res.map((c) => (
        <div key={c.id} className="mt-2 rounded-[10px] border border-[#333] bg-[#121212] p-2.5" data-testid={`link-result-${c.id}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[13px] font-bold">{c.company ? `${c.company} · ` : ""}{c.name}</p>
            <Chip onClick={() => pick(c)} testId={`link-pick-${c.id}`}>Attach, no job</Chip>
          </div>
          {c.jobs.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{c.jobs.map((j) => <Chip key={j.id} onClick={() => pick(c, j)} testId={`link-job-${j.id}`}>Job #{j.number} {j.title}</Chip>)}</div>}
          {c.quotes.length > 0 && <p className="mt-1.5 text-[10px] text-[#666]">Quotes: {c.quotes.map((d) => `#${d.number} ${d.title || ""} (${d.status})`).join(" · ")}</p>}
        </div>
      ))}
      {q.trim() && !res.length && <p className="mt-2 text-[11px] text-[#A0A0A0]" data-testid="link-none">No matching customers.</p>}
      {link && <button onClick={() => setEditing(false)} className="mt-2 text-[11px] font-bold text-[#A0A0A0]">Cancel</button>}
    </div>
  );
};

// "Open in Quote Builder" for Invoicing screens: reopens the attached builder quote or starts a new one already attached.
export const OpenInBuilder = ({ customerId, customerName, jobId, jobNumber, jobTitle, docId, testId = "open-in-builder" }) => {
  const nav = useNavigate();
  const go = async () => {
    const qs = new URLSearchParams(Object.entries({ customer_id: customerId, job_id: jobId || "", doc_id: docId || "" }).filter(([, v]) => v));
    let open = "";
    try { const hits = await api(`/quote-builder/linked?${qs}`); open = hits[0]?.id || ""; } catch {}
    const p = new URLSearchParams(Object.entries({ open, customer_id: customerId, customer_name: customerName || "", job_id: jobId || "", job_number: jobNumber || "", job_title: jobTitle || "" }).filter(([, v]) => v));
    nav(`/sales-coach/door-quote?${p}`);
  };
  if (!customerId) return null;
  return <button type="button" onClick={go} data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-[#4CCC06] px-3 py-1.5 text-[11px] font-extrabold text-[#2f8a00] transition-colors hover:bg-[#4CCC061A]"><Ion name="calculator-outline" size={13} color="#2f8a00" />Open in Quote Builder</button>;
};
